import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Banknote,
  Briefcase,
  Building2,
  CheckCircle2,
  Clock3,
  Eye,
  FileCheck2,
  Loader2,
  MapPin,
  PauseCircle,
  PlayCircle,
  Radar,
  Search,
  Send,
  StopCircle,
  UsersRound,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { adminAPI } from '../../services/api';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const textareaClass = 'min-h-[96px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'filled', label: 'Filled' },
  { value: 'expired', label: 'Expired' },
  { value: 'deleted', label: 'Deleted' }
];

const jobTypeOptions = [
  { value: 'all', label: 'All job types' },
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contract', label: 'Contract' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'internship', label: 'Internship' }
];

const remoteOptions = [
  { value: 'all', label: 'All work modes' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'on_site', label: 'On site' },
  { value: 'flexible', label: 'Flexible' }
];

const readinessOptions = [
  { value: 'all', label: 'All readiness' },
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' }
];

const sortOptions = [
  { value: 'updated_at', label: 'Recently updated' },
  { value: 'created_at', label: 'Newest' },
  { value: 'published_at', label: 'Published' },
  { value: 'title', label: 'Title' },
  { value: 'status', label: 'Status' },
  { value: 'completion', label: 'Readiness' },
  { value: 'matches', label: 'Matches' },
  { value: 'views', label: 'Views' }
];

const actionOptions = [
  { status: 'active', label: 'Activate', icon: PlayCircle, tone: 'emerald' },
  { status: 'paused', label: 'Pause', icon: PauseCircle, tone: 'amber' },
  { status: 'filled', label: 'Mark filled', icon: CheckCircle2, tone: 'sky' },
  { status: 'expired', label: 'Expire', icon: StopCircle, tone: 'rose' }
];

const labelize = (value) => String(value || 'Not set')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDate = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const formatMoney = (job) => {
  if (job.payRangeMin || job.payRangeMax) {
    const min = job.payRangeMin ? formatNumber(job.payRangeMin) : null;
    const max = job.payRangeMax ? formatNumber(job.payRangeMax) : null;
    const range = [min, max].filter(Boolean).join(' - ');
    return `${job.payCurrency || job.currency || 'USD'} ${range}${job.payPeriod ? ` / ${labelize(job.payPeriod).toLowerCase()}` : ''}`;
  }
  return job.salaryRange || 'Not set';
};

const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'complete', 'completed', 'verified'].includes(normalized)) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (['draft', 'paused', 'pending', 'in_review'].includes(normalized)) return 'border-amber-100 bg-amber-50 text-amber-700';
  if (['expired', 'filled'].includes(normalized)) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (['deleted', 'suspended', 'rejected', 'failed'].includes(normalized)) return 'border-rose-100 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const actionTone = (tone) => {
  if (tone === 'emerald') return 'border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100';
  if (tone === 'amber') return 'border-amber-100 bg-amber-50 text-amber-700 hover:bg-amber-100';
  if (tone === 'rose') return 'border-rose-100 bg-rose-50 text-rose-700 hover:bg-rose-100';
  return 'border-sky-100 bg-sky-50 text-sky-700 hover:bg-sky-100';
};

const Badge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(status)}`}>
    {labelize(status)}
  </span>
);

const StatCard = ({ icon: Icon, label, value, detail }) => (
  <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-semibold text-slate-600">{label}</h3>
      <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100/50">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {detail && <p className="text-xs font-medium text-slate-400 mt-2">{detail}</p>}
    </div>
  </div>
);

function DetailItem({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-medium text-slate-800">{value || 'Not set'}</p>
    </div>
  );
}

const availableActionsFor = (job) => {
  if (!job || job.status === 'deleted') return [];
  const transitions = {
    draft: ['active', 'paused', 'expired'],
    active: ['paused', 'filled', 'expired'],
    paused: ['active', 'filled', 'expired'],
    filled: ['active'],
    expired: ['active']
  };
  const allowed = transitions[job.status] || [];
  return actionOptions.filter((action) => allowed.includes(action.status));
};

const JobsSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-20 rounded-md" />
        <SkeletonBlock className="h-9 w-64 rounded-lg" />
        <SkeletonBlock className="h-4 w-full max-w-2xl rounded-md" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-28 rounded-[24px]" />)}
      </div>
      <SkeletonBlock className="h-[540px] rounded-[24px]" />
    </div>
  </div>
);

function JobDetailsModal({ job, onClose, onAction, onTriggerMatching, matchingBusy, matchingDisabled }) {
  if (!job) return null;
  const actions = availableActionsFor(job);
  const canRunMatching = job.status === 'active' && !matchingDisabled;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
      <section className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Job profile</p>
            <h2 className="mt-2 truncate text-2xl font-semibold tracking-tight text-slate-900">{job.title}</h2>
            <p className="mt-1 text-sm text-slate-500">{job.employer?.companyName || 'Employer not set'}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DetailItem label="Status" value={labelize(job.status)} />
            <DetailItem label="Readiness" value={`${Math.round(job.completionPercentage || 0)}%`} />
            <DetailItem label="Matches" value={formatNumber(job.stats?.matches)} />
            <DetailItem label="Positions" value={`${formatNumber(job.positionsFilled)} filled / ${formatNumber(job.positionsAvailable)} total`} />
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-5">
              <section className="rounded-[18px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-900">Role details</h3>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-2">
                  <DetailItem label="Job type" value={labelize(job.jobType)} />
                  <DetailItem label="Work mode" value={labelize(job.remotePolicy)} />
                  <DetailItem label="Location" value={job.location} />
                  <DetailItem label="Experience level" value={labelize(job.experienceLevel)} />
                  <DetailItem label="Salary" value={formatMoney(job)} />
                  <DetailItem label="Target start" value={formatDate(job.targetStartDate)} />
                  <DetailItem label="Match band" value={job.matchBandPreference} />
                  <DetailItem label="Posting method" value={labelize(job.postingMethod)} />
                </div>
              </section>

              <section className="rounded-[18px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-900">Matching and hiring activity</h3>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-2">
                  <DetailItem label="Matches" value={`${formatNumber(job.stats?.matches)} total, ${formatNumber(job.stats?.mutualMatches)} mutual`} />
                  <DetailItem label="Interest" value={`${formatNumber(job.stats?.employerInterests)} employer, ${formatNumber(job.stats?.candidateInterests)} candidate`} />
                  <DetailItem label="Average match score" value={job.stats?.averageMatchScore === null ? null : `${Math.round(job.stats?.averageMatchScore)}%`} />
                  <DetailItem label="Interviews" value={`${formatNumber(job.stats?.interviews)} total, ${formatNumber(job.stats?.upcomingInterviews)} upcoming`} />
                  <DetailItem label="Assessments" value={`${formatNumber(job.stats?.assessments)} created, ${formatNumber(job.stats?.assessmentAttempts)} attempts`} />
                  <DetailItem label="Offers" value={`${formatNumber(job.stats?.offers)} total, ${formatNumber(job.stats?.openOffers)} open`} />
                  <DetailItem label="Requirements" value={`${formatNumber(job.stats?.requirements)} legacy, ${formatNumber(job.stats?.requirementTags)} canonical`} />
                  <DetailItem label="Signals" value={`${formatNumber(job.stats?.profileAnswers)} answers, ${formatNumber(job.stats?.materializedSignals)} signals`} />
                </div>
              </section>

              <section className="rounded-[18px] border border-slate-100 bg-white">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="font-semibold text-slate-900">Description</h3>
                </div>
                <div className="space-y-3 p-4">
                  <DetailItem label="Description" value={job.description} />
                  <DetailItem label="Requirements text" value={job.requirementsText} />
                  <DetailItem label="Non-negotiables" value={Array.isArray(job.nonNegotiableFilters) && job.nonNegotiableFilters.length ? job.nonNegotiableFilters.join(', ') : null} />
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-[18px] border border-slate-100 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-900">Employer</h3>
                <div className="mt-3 space-y-3">
                  <DetailItem label="Company" value={job.employer?.companyName} />
                  <DetailItem label="Account" value={labelize(job.employer?.accountStatus)} />
                  <DetailItem label="KYC" value={labelize(job.employer?.verificationStatus)} />
                  <DetailItem label="Contact" value={job.employer?.contactEmail} />
                </div>
              </section>

              <section className="rounded-[18px] border border-slate-100 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-900">Dates</h3>
                <div className="mt-3 space-y-3">
                  <DetailItem label="Created" value={formatDateTime(job.createdAt)} />
                  <DetailItem label="Published" value={formatDateTime(job.publishedAt)} />
                  <DetailItem label="Expires" value={formatDateTime(job.expiresAt)} />
                  <DetailItem label="Updated" value={formatDateTime(job.updatedAt)} />
                </div>
              </section>

              <button
                type="button"
                onClick={() => onTriggerMatching(job)}
                disabled={!canRunMatching || matchingBusy}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                title={job.status === 'active' ? 'Run matching for this job' : 'Only active jobs can be matched'}
              >
                {matchingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                Run matching
              </button>

              {!!actions.length && (
                <div className="grid gap-2">
                  {actions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.status}
                        type="button"
                        onClick={() => onAction(job, action.status)}
                        className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition ${actionTone(action.tone)}`}
                      >
                        <Icon className="h-4 w-4" />
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function JobStatusModal({ action, onClose, onSubmit, saving }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    setReason('');
  }, [action?.job?.id, action?.status]);

  if (!action) return null;
  const option = actionOptions.find((item) => item.status === action.status);
  const title = option?.label || 'Update status';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
      <section className="w-full max-w-lg overflow-hidden rounded-[22px] bg-white shadow-2xl">
        <div className="border-b border-slate-100 p-5">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            This changes the job status in the admin system. Activation is blocked by the API if the job is not complete.
          </p>
        </div>
        <div className="p-5">
          <p className="mb-3 text-sm font-medium text-slate-700">{action.job?.title}</p>
          <label className="block text-sm font-medium text-slate-700">Reason (optional)</label>
          <textarea
            className={`${textareaClass} mt-2`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Record why this status is changing."
          />
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={saving}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Update
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white px-6 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-500">
        <Briefcase className="h-6 w-6" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900">No jobs found</h2>
      <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
        Adjust the search or filters to view job postings across employers.
      </p>
      <button type="button" onClick={onReset} className="mt-5 inline-flex h-10 items-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
        Reset filters
      </button>
    </div>
  );
}

function JobsListPage() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ statuses: [], readiness: {} });
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    jobType: 'all',
    remotePolicy: 'all',
    readiness: 'all',
    sortBy: 'updated_at',
    sortOrder: 'DESC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [savingAction, setSavingAction] = useState(false);
  const [matchingJobId, setMatchingJobId] = useState(null);

  const loadJobs = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await adminAPI.getJobs({
        ...filters,
        limit: pagination.limit,
        offset
      });
      setJobs(response.jobs || []);
      setStats(response.stats || { statuses: [], readiness: {} });
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Jobs could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadJobs({ offset: 0 });
  }, [loadJobs]);

  const totals = useMemo(() => {
    const active = stats.statuses?.find((item) => item.status === 'active')?.count || 0;
    const draft = stats.statuses?.find((item) => item.status === 'draft')?.count || 0;
    const paused = stats.statuses?.find((item) => item.status === 'paused')?.count || 0;
    return {
      total: stats.total || pagination.total,
      active,
      draft,
      paused,
      complete: stats.readiness?.complete || 0,
      averageCompletion: stats.readiness?.averageCompletion || 0
    };
  }, [pagination.total, stats]);

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: 'all',
      jobType: 'all',
      remotePolicy: 'all',
      readiness: 'all',
      sortBy: 'updated_at',
      sortOrder: 'DESC'
    });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const nextSearch = searchInput.trim();
    if (nextSearch === filters.search) {
      loadJobs({ offset: 0 });
      return;
    }
    setFilters((current) => ({ ...current, search: nextSearch }));
  };

  const updateJobStatus = async (reason) => {
    if (!statusAction) return;
    setSavingAction(true);

    try {
      await adminAPI.updateAdminJobStatus(statusAction.job.id, {
        status: statusAction.status,
        reason: reason.trim()
      });
      toast.success('Job status updated.');
      setStatusAction(null);
      setSelectedJob(null);
      await loadJobs({ offset: pagination.offset, quiet: true });
    } catch (actionError) {
      toast.error(extractError(actionError, 'Job status could not be updated.'));
    } finally {
      setSavingAction(false);
    }
  };

  const triggerJobMatching = async (job) => {
    if (!job || matchingJobId || job.status !== 'active') return;
    setMatchingJobId(job.id);

    try {
      const response = await adminAPI.triggerAdminJobMatching(job.id, {
        reason: 'Manual admin trigger from job list',
        refreshFeatures: true
      });
      const matching = response.matching || {};
      const candidatesEvaluated = formatNumber(matching.candidatesEvaluated);
      const matchesCreated = formatNumber(matching.matchesCreated);

      toast.success(`Matching completed: ${matchesCreated} matches saved from ${candidatesEvaluated} candidates.`);
      await loadJobs({ offset: pagination.offset, quiet: true });

      if (selectedJob && String(selectedJob.id) === String(job.id)) {
        setSelectedJob((current) => {
          if (!current || String(current.id) !== String(job.id)) return current;
          return {
            ...current,
            stats: {
              ...(current.stats || {}),
              matches: matching.after?.totalMatches ?? current.stats?.matches,
              mutualMatches: matching.after?.mutualMatches ?? current.stats?.mutualMatches,
              employerInterests: matching.after?.employerInterests ?? current.stats?.employerInterests,
              candidateInterests: matching.after?.candidateInterests ?? current.stats?.candidateInterests,
              averageMatchScore: matching.after?.averageMatchScore ?? current.stats?.averageMatchScore
            }
          };
        });
      }
    } catch (matchingError) {
      toast.error(extractError(matchingError, 'Matching could not be triggered.'));
    } finally {
      setMatchingJobId(null);
    }
  };

  if (loading) return <JobsSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Job list</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Publication state, employer ownership, readiness, salary, matching activity, interviews, assessments, offers, and recent operational signals.
            </p>
          </div>
          
        </div>

        {error && (
          <div className="rounded-[18px] border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={Briefcase} label="Jobs" value={formatNumber(totals.total)} detail={`${formatNumber(totals.active)} active`} />
          <StatCard icon={Clock3} label="Draft and paused" value={formatNumber(totals.draft + totals.paused)} detail={`${formatNumber(totals.draft)} draft, ${formatNumber(totals.paused)} paused`} />
          <StatCard icon={FileCheck2} label="Complete jobs" value={formatNumber(totals.complete)} detail={`${Math.round(totals.averageCompletion)}% average readiness`} />
          <StatCard icon={UsersRound} label="Loaded rows" value={formatNumber(jobs.length)} detail={`${formatNumber(pagination.total)} matching filters`} />
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4 sm:p-5">
            <form onSubmit={submitSearch} className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1">
              <label className="relative block min-w-[280px] flex-[1_0_320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${inputClass} w-full pl-9`}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search title, company, location, seniority, job ID..."
                />
              </label>
              <select className={`${inputClass} w-[150px] shrink-0`} value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
                {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[150px] shrink-0`} value={filters.jobType} onChange={(event) => setFilters((current) => ({ ...current, jobType: event.target.value }))}>
                {jobTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[155px] shrink-0`} value={filters.remotePolicy} onChange={(event) => setFilters((current) => ({ ...current, remotePolicy: event.target.value }))}>
                {remoteOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[145px] shrink-0`} value={filters.readiness} onChange={(event) => setFilters((current) => ({ ...current, readiness: event.target.value }))}>
                {readinessOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[170px] shrink-0`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button type="submit" className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
                Search
              </button>
            </form>
          </div>

          {!jobs.length ? (
            <div className="p-5">
              <EmptyState onReset={resetFilters} />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-[1420px] divide-y divide-slate-100">
                  <thead className="bg-slate-50/50">
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">Job</th>
                      <th className="px-6 py-4">Employer</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Pay</th>
                      <th className="px-6 py-4">Hiring</th>
                      <th className="px-6 py-4">Activity</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {jobs.map((job) => {
                      const actions = availableActionsFor(job);
                      return (
                        <tr key={job.id} className="align-top transition hover:bg-slate-50/80">
                          <td className="px-6 py-4">
                            <p className="max-w-[260px] truncate font-semibold text-slate-900">{job.title}</p>
                            <p className="mt-1 text-sm text-slate-500">#{job.id}</p>
                            <p className="mt-1 max-w-[260px] truncate text-xs text-slate-500">{job.description || 'No description'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-medium text-slate-800">{job.employer?.companyName || 'Employer not set'}</p>
                            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Building2 className="h-3.5 w-3.5" /> {job.employer?.industry || job.employer?.sector || 'Industry not set'}</p>
                            <div className="mt-2"><Badge status={job.employer?.verificationStatus} /></div>
                          </td>
                          <td className="px-6 py-4">
                            <Badge status={job.status} />
                            <p className="mt-2 text-xs text-slate-500">{Math.round(job.completionPercentage || 0)}% ready</p>
                            <p className="mt-1 text-xs text-slate-500">{job.isComplete ? 'Complete' : 'Needs setup'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-semibold text-slate-900">{labelize(job.jobType)}</p>
                            <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> {job.location || 'Location not set'}</p>
                            <p className="mt-1 text-xs text-slate-500">{labelize(job.remotePolicy)} · {labelize(job.experienceLevel)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-semibold text-slate-900">{formatMoney(job)}</p>
                            <p className="mt-1 text-xs text-slate-500">{formatNumber(job.positionsFilled)} filled / {formatNumber(job.positionsAvailable)} positions</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-semibold text-slate-900">{formatNumber(job.stats?.matches)} matches</p>
                            <p className="mt-1 text-xs text-slate-500">{formatNumber(job.stats?.interviews)} interviews, {formatNumber(job.stats?.assessments)} assessments</p>
                            <p className="mt-1 text-xs text-slate-500">{formatNumber(job.stats?.offers)} offers, {formatNumber(job.stats?.openOffers)} open</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-700">{formatDate(job.createdAt)}</p>
                            <p className="mt-1 text-xs text-slate-500">Published: {formatDate(job.publishedAt, 'Not published')}</p>
                            <p className="mt-1 text-xs text-slate-500">Updated: {formatDate(job.updatedAt)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => setSelectedJob(job)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Job">
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => triggerJobMatching(job)}
                                disabled={job.status !== 'active' || Boolean(matchingJobId)}
                                className="flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600 shadow-sm transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-45"
                                title={job.status === 'active' ? 'Run matching' : 'Only active jobs can be matched'}
                              >
                                {matchingJobId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                              </button>
                              {actions.slice(0, 1).map((action) => {
                                const Icon = action.icon;
                                return (
                                  <button key={action.status} type="button" onClick={() => setStatusAction({ job, status: action.status })} className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors shadow-sm border ${actionTone(action.tone)}`} title={action.label}>
                                    <Icon className="w-4 h-4" />
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 xl:hidden">
                {jobs.map((job) => {
                  const actions = availableActionsFor(job);
                  return (
                    <article key={job.id} className="rounded-[24px] border border-slate-100 bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.035)]">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="truncate font-semibold text-slate-900">{job.title}</h2>
                          <p className="mt-1 text-sm text-slate-500">{job.employer?.companyName || 'Employer not set'}</p>
                        </div>
                        <Badge status={job.status} />
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {job.location || 'Location not set'}</p>
                        <p className="flex items-center gap-2 text-sm text-slate-600"><Briefcase className="h-4 w-4 text-slate-400" /> {labelize(job.jobType)} · {labelize(job.remotePolicy)}</p>
                        <p className="flex items-center gap-2 text-sm text-slate-600"><Banknote className="h-4 w-4 text-slate-400" /> {formatMoney(job)}</p>
                        <p className="flex items-center gap-2 text-sm text-slate-600"><Send className="h-4 w-4 text-slate-400" /> {formatDate(job.publishedAt, 'Not published')}</p>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-lg font-bold text-slate-900">{Math.round(job.completionPercentage || 0)}%</p>
                          <p className="text-xs text-slate-500">Ready</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-lg font-bold text-slate-900">{formatNumber(job.stats?.matches)}</p>
                          <p className="text-xs text-slate-500">Matches</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-lg font-bold text-slate-900">{formatNumber(job.stats?.offers)}</p>
                          <p className="text-xs text-slate-500">Offers</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                        <Badge status={job.employer?.verificationStatus} />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setSelectedJob(job)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Job">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => triggerJobMatching(job)}
                            disabled={job.status !== 'active' || Boolean(matchingJobId)}
                            className="flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600 shadow-sm transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-45"
                            title={job.status === 'active' ? 'Run matching' : 'Only active jobs can be matched'}
                          >
                            {matchingJobId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                          </button>
                          {actions.slice(0, 1).map((action) => {
                            const Icon = action.icon;
                            return (
                              <button key={action.status} type="button" onClick={() => setStatusAction({ job, status: action.status })} className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors shadow-sm border ${actionTone(action.tone)}`} title={action.label}>
                                    <Icon className="w-4 h-4" />
                                  </button>
                            );
                          })}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  Showing {formatNumber(pagination.offset + 1)} - {formatNumber(pagination.offset + jobs.length)} of {formatNumber(pagination.total)}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.offset <= 0 || refreshing}
                    onClick={() => loadJobs({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })}
                    className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={!pagination.hasMore || refreshing}
                    onClick={() => loadJobs({ offset: pagination.offset + pagination.limit, quiet: true })}
                    className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <JobDetailsModal
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        onAction={(job, status) => setStatusAction({ job, status })}
        onTriggerMatching={triggerJobMatching}
        matchingBusy={matchingJobId === selectedJob?.id}
        matchingDisabled={Boolean(matchingJobId)}
      />
      <JobStatusModal
        action={statusAction}
        onClose={() => setStatusAction(null)}
        onSubmit={updateJobStatus}
        saving={savingAction}
      />
    </div>
  );
}

export default JobsListPage;
